import React, { Suspense, lazy } from "react";
import { motion } from "framer-motion";
import {
  Building2,
  ShieldCheck,
  CheckCircle2,
  ArrowRight,
  Search,
  Wrench,
  Receipt,
  Sparkles,
  BarChart3,
  Briefcase,
  Star,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { LeadConsultationForm } from "@/components/LeadConsultationForm";

// Lazy-load below-the-fold heavy components so they don't block initial paint.
const RentVsSellCalculator = lazy(() =>
  import("@/components/RentVsSellCalculator").then((m) => ({
    default: m.RentVsSellCalculator,
  })),
);
const GoogleReviewsWidget = lazy(() =>
  import("@/components/GoogleReviewsWidget").then((m) => ({
    default: m.GoogleReviewsWidget,
  })),
);

export const IndexPage: React.FC<{ onOpenConsultation: () => void }> = ({
  onOpenConsultation,
}) => {
  const serviceCards = [
    {
      title: "Full Property Management",
      icon: Building2,
      desc: "Complete end-to-end management for residential and commercial assets with full financial transparency.",
      link: "/property-management",
    },
    {
      title: "Strategic Marketing & Leasing",
      icon: Search,
      desc: "High-impact multi-channel marketing campaigns that minimize vacancy and attract high-caliber tenants.",
      link: "/marketing",
    },
    {
      title: "Rigorous Tenant Screening",
      icon: ShieldCheck,
      desc: "Comprehensive credit, background, income, and nationwide eviction checks to protect your property.",
      link: "/tenant-screening",
    },
    {
      title: "Automated Rent Collection",
      icon: Receipt,
      desc: "Seamless digital tenant payments with direct owner disbursements and zero friction.",
      link: "/rent-collection",
    },
    {
      title: "24/7 Maintenance & Care",
      icon: Wrench,
      desc: "Vetted network of trusted insured vendors responding round-the-clock with competitive pricing.",
      link: "/maintenance-services",
    },
    {
      title: "Transparent Accounting",
      icon: BarChart3,
      desc: "Real-time financial portals, monthly detailed statements, and 1099 tax preparation.",
      link: "/accounting",
    },
  ];

  const processSteps = [
    {
      number: "01",
      title: "Property Analysis",
      desc: "In-depth market valuation, rental yield analysis, and custom management strategy formulation.",
    },
    {
      number: "02",
      title: "Seamless Onboarding",
      desc: "We handle tenant transition, lease audits, digital portal setup, and vendor coordination effortless.",
    },
    {
      number: "03",
      title: "Strategic Leasing",
      desc: "Professional photography, targeted digital syndication, and 360-degree candidate vetting.",
    },
    {
      number: "04",
      title: "Passive Asset Optimization",
      desc: "Enjoy guaranteed rent collections, proactive maintenance, and monthly financial disbursements.",
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100">
      {/* FULL SCREEN HERO SECTION */}
      <section className="relative min-h-[90vh] flex items-center justify-center pt-24 pb-16 overflow-hidden bg-slate-900 text-white">
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            className="w-full h-full object-cover object-center opacity-95"
            aria-hidden="true"
          >
            <source
              src="https://assets.cdn.filesafe.space/mT80PbegrbDSOV5X6nOi/media/6a7b209d1c6fe2d33635f992.mp4"
              type="video/mp4"
            />
          </video>
          <img
            src="https://vibe.filesafe.space/1785535147473014979/assets/624260c0-d7df-4138-8285-615546716c65.jpg"
            srcSet="https://vibe.filesafe.space/1785535147473014979/assets/045f725d-013f-4370-b13e-c868934893bb.jpg 640w, https://vibe.filesafe.space/1785535147473014979/assets/624260c0-d7df-4138-8285-615546716c65.jpg 1280w"
            sizes="100vw"
            width={1280}
            height={720}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover opacity-95"
            loading="eager"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-900/70 to-slate-900/50" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-6 max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-accent text-xs font-bold uppercase tracking-wider">
              <Sparkles className="w-4 h-4 text-accent" /> Customer-Focused &
              Technologically Savvy
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight text-white font-sans">
              Property Management Built on{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 via-accent to-amber-200">
                Integrity, Tech & Growth
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-slate-300 font-light max-w-3xl mx-auto leading-relaxed">
              Feingold Property Management, LLC is one of the most engaging,
              innovative management companies in the nation. Specialized in
              finding the perfect fit for clients, properties, and communities
              both commercially and residentially.
            </p>

            {/* Property Type Selector */}
            <div className="flex flex-col items-center justify-center gap-3 pt-6">
              <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                Select Your Property Type
              </span>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 w-full max-w-md">
                <Link
                  to="/pricing?type=residential"
                  className="w-full sm:w-auto"
                >
                  <button className="group flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 rounded-xl bg-white/10 hover:bg-accent hover:text-slate-900 border border-white/20 hover:border-accent text-white font-bold text-sm backdrop-blur-md transition-all duration-300">
                    <Building2 className="w-4 h-4" />
                    Residential
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                  </button>
                </Link>
                <Link
                  to="/pricing?type=commercial"
                  className="w-full sm:w-auto"
                >
                  <button className="group flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 rounded-xl bg-white/10 hover:bg-accent hover:text-slate-900 border border-white/20 hover:border-accent text-white font-bold text-sm backdrop-blur-md transition-all duration-300">
                    <Briefcase className="w-4 h-4" />
                    Commercial
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                  </button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* INTRODUCTION & WHY FEINGOLD */}
      <section className="py-20 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <span className="text-xs font-extrabold uppercase tracking-widest text-primary bg-primary/10 px-3 py-1 rounded-md">
                About Feingold Property Management
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                A Modern Management Partner Dedicated to Excellence &
                Responsibility
              </h2>
              <p className="text-slate-600 dark:text-slate-300 text-base leading-relaxed">
                Feingold Property Management, LLC (Feingold) stands as one of
                the most engaging, customer-focused, innovative, and
                technologically savvy management companies in the country today.
              </p>
              <p className="text-slate-600 dark:text-slate-300 text-base leading-relaxed">
                With unparalleled growth potential across both commercial and
                residential sectors, our team specializes in finding the precise
                fit for our clients, properties, and communities. We are
                committed to a culture of diversity and corporate responsibility
                to serve every client with absolute integrity and transparency.
              </p>

              <div className="space-y-3 pt-2">
                {[
                  "State-of-the-art digital owner & resident portals",
                  "Proactive maintenance & 24/7 vendor response",
                  "Rigorous multi-stage tenant screening process",
                  "Fostering strong relationships & sustainable growth",
                ].map((point, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0" />
                    <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
                      {point}
                    </span>
                  </div>
                ))}
              </div>

              <div className="pt-4">
                <Link to="/about">
                  <Button className="bg-slate-900 hover:bg-slate-800 text-white font-semibold">
                    Learn More About Our Culture{" "}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Visual Box */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-slate-200/80">
                <img
                  src="https://vibe.filesafe.space/1785535147473014979/assets/a13f75c1-d283-43c1-9a53-9f4d78c627da.jpg"
                  srcSet="https://vibe.filesafe.space/1785535147473014979/assets/a13f75c1-d283-43c1-9a53-9f4d78c627da.jpg 480w, https://vibe.filesafe.space/1785535147473014979/assets/a13f75c1-d283-43c1-9a53-9f4d78c627da.jpg 960w"
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  width={960}
                  height={640}
                  alt="Feingold Commercial & Residential Community"
                  className="w-full h-[420px] object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent flex items-end p-6">
                  <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-md p-4 rounded-xl border border-white/20 shadow-lg w-full">
                    <div className="text-sm font-bold text-slate-900 dark:text-white">
                      Commercial & Residential Excellence
                    </div>
                    <div className="text-xs text-slate-500">
                      Unlocking full long-term value for property owners
                      nationwide.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section className="py-20 bg-slate-100 dark:bg-slate-950/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-extrabold uppercase tracking-widest text-primary bg-primary/10 px-3 py-1 rounded-md">
              Comprehensive Solutions
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white mt-3">
              Comprehensive Property Services
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-sm sm:text-base mt-2">
              From strategic marketing and background vetting to automated
              accounting, we handle every detail with precision.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {serviceCards.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  className="bg-white dark:bg-slate-900 rounded-2xl p-8 shadow-md hover:shadow-xl border border-slate-200/80 dark:border-slate-800 transition-all duration-300 flex flex-col justify-between group"
                >
                  <div>
                    <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                      {service.title}
                    </h3>
                    <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed mb-6">
                      {service.desc}
                    </p>
                  </div>

                  <Link
                    to={service.link}
                    className="inline-flex items-center text-sm font-bold text-primary hover:text-primary/80 transition-colors pt-2 border-t border-slate-100 dark:border-slate-800"
                  >
                    View Details & Benefits{" "}
                    <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CALCULATOR SECTION */}
      <section className="py-20 bg-white dark:bg-slate-900 border-y border-slate-200/60 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Suspense fallback={<div className="h-96" />}>
            <RentVsSellCalculator />
          </Suspense>
        </div>
      </section>

      {/* PROCESS SECTION */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-xs font-bold text-accent uppercase tracking-widest bg-accent/10 px-3 py-1 rounded-md">
              Streamlined Operations
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mt-3">
              The Feingold Management Process
            </h2>
            <p className="text-slate-400 text-sm mt-2">
              Designed for total peace of mind and maximum investment returns.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, idx) => (
              <div
                key={idx}
                className="relative bg-slate-800/60 border border-slate-700/80 rounded-2xl p-6 flex flex-col justify-between"
              >
                <div>
                  <span className="text-4xl font-black text-accent/80 block mb-4">
                    {step.number}
                  </span>
                  <h3 className="text-lg font-bold text-white mb-2">
                    {step.title}
                  </h3>
                  <p className="text-slate-400 text-xs leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 bg-slate-50 dark:bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-xs font-extrabold uppercase tracking-widest text-primary bg-primary/10 px-3 py-1 rounded-md">
              Verified Client Feedback
            </span>
            <div className="flex flex-col items-center justify-center gap-2">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white">
                Trusted by Property Owners and Residents
              </h2>
              <div className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-900 px-4 py-1.5 rounded-full border border-slate-200/80 shadow-sm mt-1">
                <span className="flex items-center gap-1 text-amber-700 font-bold">
                  <Star className="w-4 h-4 fill-amber-400 text-amber-500" /> 5.0
                </span>
                <span>•</span>
                <span>Verified Google Reviews (Laurel, MD)</span>
              </div>
            </div>
          </div>

          <div className="max-w-5xl mx-auto">
            <Suspense fallback={<div className="h-96" />}>
              <GoogleReviewsWidget />
            </Suspense>
          </div>
        </div>
      </section>

      {/* CALL TO ACTION CONSULTATION FORM */}
      <section className="py-20 bg-gradient-to-br from-primary via-slate-900 to-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5 space-y-6">
              <span className="text-xs font-bold uppercase tracking-widest text-accent bg-accent/10 px-3 py-1 rounded-md">
                Get Started Today
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                Ready to Experience Stress-Free Property Management?
              </h2>
              <p className="text-slate-300 text-sm leading-relaxed">
                Connect with our expert property management team for a
                complimentary rental market analysis and property consultation.
              </p>

              <div className="space-y-4 pt-2">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-white/10 text-accent">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-semibold text-slate-200">
                    Zero Obligation Rental Valuation
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-white/10 text-accent">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-semibold text-slate-200">
                    Transparent Flat-Fee Options Available
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-white/10 text-accent">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-semibold text-slate-200">
                    24/7 Digital Portal Access
                  </span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <LeadConsultationForm title="Schedule Your Rental Strategy Session" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
